import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClockCounterClockwise, ArrowClockwise, Warning, ArrowUUpLeft, FilePdf } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, formatUnit } from "@/lib/lorryApi";
import { downloadWeeklyDigestPdf } from "@/lib/weeklyDigestPdf";

function fmtLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function HistorySheet({ open, onOpenChange, onResetDone }) {
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [digesting, setDigesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/history/runs");
      setRuns(res.data);
      if (res.data.length && !activeRun) {
        setActiveRun(res.data[0].id);
      }
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [activeRun]);

  const loadEntries = useCallback(async (runId) => {
    if (!runId) {
      setEntries([]);
      return;
    }
    try {
      const res = await api.get(`/history/lorries?run_id=${runId}`);
      setEntries(res.data);
    } catch (e) {
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (activeRun) loadEntries(activeRun);
  }, [activeRun, loadEntries]);

  const doReset = async () => {
    setResetting(true);
    try {
      const res = await api.post("/admin/reset-now");
      toast.success(
        `Archived ${res.data.lorries_archived} lorries`
      );
      setConfirmReset(false);
      // Jump to the freshly created run so Restore buttons appear immediately
      if (res.data?.id) setActiveRun(res.data.id);
      await load();
      onResetDone?.();
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const restoreEntry = async (entry) => {
    setRestoringId(entry.id);
    try {
      await api.post(`/history/lorries/${entry.id}/restore`);
      toast.success(`${entry.registration} restored to the yard`);
      onResetDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoringId(null);
    }
  };

  const downloadDigest = async () => {
    setDigesting(true);
    try {
      const res = await api.get("/history/digest");
      const data = res.data;
      if ((data?.totals?.lorries ?? 0) === 0) {
        toast.message("No activity last week — downloading empty digest anyway");
      }
      const name = downloadWeeklyDigestPdf(data);
      toast.success(`Digest downloaded · ${name.split("-").slice(-1)[0].replace(".pdf", "")}`);
    } catch (e) {
      toast.error("Failed to build digest");
    } finally {
      setDigesting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          data-testid="history-sheet"
          side="right"
          className="bg-zinc-950 border-l border-zinc-800 rounded-none w-full sm:max-w-[560px] p-0 overflow-y-auto"
        >
          <div className="p-6 border-b border-zinc-800">
            <SheetHeader className="text-left">
              <span className="overline text-yellow-500">Archive</span>
              <SheetTitle className="font-display text-2xl font-black tracking-tight text-zinc-100">
                Reset History
              </SheetTitle>
              <SheetDescription className="text-zinc-500 text-sm">
                The yard auto-resets daily at 18:00 London time. Each reset archives
                the day's lorries here for review, while drivers and lorry plates
                stay remembered so tomorrow's autocomplete still works.
              </SheetDescription>
            </SheetHeader>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              data-testid="manual-reset-btn"
              onClick={() => setConfirmReset(true)}
              variant="outline"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-red-400 hover:border-red-500/40 hover:bg-zinc-900"
            >
              <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
              <span className="text-xs tracking-widest uppercase font-semibold">Reset now</span>
            </Button>
            <Button
              data-testid="weekly-digest-btn"
              onClick={downloadDigest}
              disabled={digesting}
              variant="outline"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-yellow-500 hover:border-yellow-500/40 hover:bg-zinc-900"
            >
              <FilePdf size={14} weight="bold" className="mr-1.5" />
              <span className="text-xs tracking-widest uppercase font-semibold">
                {digesting ? "Building…" : "Weekly PDF"}
              </span>
            </Button>
          </div>
          </div>

          <div className="p-6">
            <div className="overline text-zinc-500 mb-3">
              {runs.length} reset{runs.length === 1 ? "" : "s"} on record
            </div>

            {loading && (
              <div className="text-zinc-500 text-sm">Loading…</div>
            )}

            {!loading && runs.length === 0 && (
              <div className="p-6 text-center border border-dashed border-zinc-800 bg-zinc-900/30">
                <p className="text-zinc-400 text-sm">
                  No resets yet. The first daily archive will appear here after 18:00 London.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {runs.map((r) => {
                const active = r.id === activeRun;
                return (
                  <button
                    key={r.id}
                    data-testid={`run-${r.id}`}
                    onClick={() => setActiveRun(r.id)}
                    className={`w-full text-left border p-3 transition-colors ${
                      active
                        ? "border-yellow-500/50 bg-yellow-500/5"
                        : "border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/70"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-zinc-100 font-mono text-sm">
                          {fmtLocal(r.local_time)}
                        </div>
                        <div className="text-[0.7rem] uppercase tracking-widest text-zinc-500 mt-0.5">
                          {r.trigger} · {r.timezone}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-black text-lg text-yellow-500 leading-none">
                          {r.lorries_archived}
                        </div>
                        <div className="overline text-zinc-500 text-[0.6rem] mt-1">Lorries</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[0.7rem] uppercase tracking-widest text-zinc-500">
                      <span>{r.drivers_archived} drivers</span>
                      <span>·</span>
                      <span>{r.registrations_archived} plates</span>
                      <span>·</span>
                      <span>{r.total_load_tons ?? 0} t</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {activeRun && entries.length > 0 && (
              <div className="mt-6 pt-6 border-t border-zinc-800">
                <div className="overline text-yellow-500 mb-3">Archived entries</div>
                <div className="space-y-2">
                  {entries.map((e) => (
                    <div
                      key={e.id}
                      data-testid={`history-entry-${e.id}`}
                      className="border border-zinc-800 bg-zinc-900/40 p-3 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-mono font-semibold text-zinc-100 tracking-wider">
                          {e.registration}
                        </span>
                        <span className="text-[0.65rem] uppercase tracking-widest text-zinc-500">
                          {e.status}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-zinc-400">
                        <span>{e.product}</span>
                        <span className="text-zinc-500">·</span>
                        <span>
                          {e.load_amount}
                          <span className="text-zinc-500 ml-0.5">
                            {formatUnit(e.load_unit)}
                          </span>
                        </span>
                        <span className="text-zinc-500">·</span>
                        <span>{e.driver_name}</span>
                        <span className="text-zinc-500">·</span>
                        <span className="font-mono">{e.date}</span>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button
                          data-testid={`restore-${e.id}`}
                          onClick={() => restoreEntry(e)}
                          disabled={restoringId === e.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.65rem] uppercase tracking-widest font-semibold text-zinc-400 hover:text-yellow-500 hover:bg-zinc-800 border border-zinc-800 hover:border-yellow-500/40 transition-colors rounded-sm disabled:opacity-50"
                        >
                          <ArrowUUpLeft size={12} weight="bold" />
                          {restoringId === e.id ? "Restoring…" : "Restore"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent
          data-testid="reset-confirm-dialog"
          className="bg-zinc-950 border border-zinc-800 rounded-none"
        >
          <AlertDialogHeader>
            <span className="overline text-red-500 inline-flex items-center gap-1.5">
              <Warning size={12} weight="fill" /> Danger
            </span>
            <AlertDialogTitle className="font-display text-2xl font-black">
              Reset the yard now?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This archives every current lorry entry into the history log and
              clears them from the live yard. Drivers and lorry plates stay
              remembered. This normally runs automatically at 18:00 London time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="reset-cancel"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm-btn"
              onClick={doReset}
              disabled={resetting}
              className="rounded-sm bg-red-600 hover:bg-red-500 text-white"
            >
              {resetting ? "Resetting…" : "Reset now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function useNextReset() {
  const [info, setInfo] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/history/next-reset");
      setInfo(res.data);
    } catch (e) {
      // silent
    }
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);
  return info;
}

export function NextResetPill({ info }) {
  if (!info) return null;
  const secs = info.seconds_until || 0;
  const hrs = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const label = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  const soon = secs < 60 * 60;
  return (
    <span
      data-testid="next-reset-pill"
      className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 border text-[0.65rem] tracking-[0.15em] uppercase font-semibold rounded-sm ${
        soon
          ? "text-red-400 border-red-500/30 bg-red-500/5"
          : "text-zinc-400 border-zinc-800 bg-zinc-900/60"
      }`}
      title={`Auto-reset at ${new Date(info.next_reset_local).toLocaleString()}`}
    >
      <ClockCounterClockwise size={11} weight="bold" />
      Reset in {label}
    </span>
  );
}
